import { useQuery } from '@tanstack/react-query';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';

import { getQueryClient } from '../../../lib/query-client';
import { headers } from '../../../queries/meta';
import { DimensionKey, ScoredBonus } from '../../../types/recognition';

enum QueryKey {
	FilterOptions = 'recognition-scores:filter-options',
	TopRecognitions = 'recognition-scores:top-recognitions',
	QualityScores = 'recognition-scores:quality-scores',
	DepartmentAverages = 'recognition-scores:department-averages',
}

export const filterOptionsQueryKey = (params: Record<string, any>) => [QueryKey.FilterOptions, params];
export const topRecognitionsQueryKey = (params: Record<string, any>) => [QueryKey.TopRecognitions, params];
export const qualityScoresQueryKey = (params: any) => [QueryKey.QualityScores, params];
export const departmentAveragesQueryKey = (params: any) => [QueryKey.DepartmentAverages, params];

export const filterOptionsPath = (prefix = '') => `${prefix}/admin/analytics/recognition_quality/filter_options`;
export const topRecognitionsPath = (prefix = '') => `${prefix}/admin/analytics/recognition_quality/top_scoring_recognition`;
export const qualityScoresPath = (prefix = '') => `${prefix}/admin/analytics/recognition_quality/averages`;
export const departmentAveragesPath = (prefix = '') => `${prefix}/admin/analytics/recognition_quality/department_averages`;

type OptionAPIData = {
	key: string;
	displayName: string;
};

type FilterOptionsAPIData = {
	hashtags: string[];
	giverOptions: {
		customProperties: Array<{
			key: string;
			displayName: string;
			values: OptionAPIData[];
		}>;
		teams: OptionAPIData[];
	};
	receiverOptions: {
		customProperties: Array<{
			key: string;
			displayName: string;
			values: OptionAPIData[];
		}>;
		teams: OptionAPIData[];
	};
};

export const datesToParams = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
	const startDateString = format(startDate, 'yyyy-MM-dd');
	const endDateString = format(endDate, 'yyyy-MM-dd');

	return {
		start_date: startDateString,
		end_date: endDateString,
	};
};

const createFilterOptionsFetcher = (params: { start_date: string; end_date: string }) => async () => {
	const http = await getHttpClient();
	// NOTE: We can't safely pass the inflection header here to convert from snake_case to camelCase because
	// the *keys*, which are what get transformed, are the names of custom properties and can't be mutated.
	return (await http.get<FilterOptionsAPIData>(filterOptionsPath(), { headers, params })).data;
};

export const loadFilterOptions = async (params: { startDate: Date; endDate: Date }) => {
	const queryClient = await getQueryClient();
	const fetchParams = datesToParams(params);

	return queryClient.fetchQuery({
		queryKey: filterOptionsQueryKey(fetchParams),
		queryFn: createFilterOptionsFetcher(fetchParams),
	});
};

export const useFilterOptions = (options: { startDate: Date; endDate: Date }) => {
	const params = datesToParams(options);

	return useQuery({
		queryKey: filterOptionsQueryKey(params),
		queryFn: createFilterOptionsFetcher(params),
	});
};

{
	/* Quality scores */
}
type DimensionAverage = {
	averageMatchRate: number;
	averageMatchRatePercentage: number;
	key: string;
	matches: number;
	occurrences: number;
};

type MonthlyAverage = {
	month: number;
	overallAveragePercentage: number;
	overallAverageScore: number;
	year: number;
};

export type QualityScoresAPIData = {
	dimensionAverages: DimensionAverage[];
	monthlyAverages: MonthlyAverage[];
	overallAveragePercentage: number;
	overallAverageScore: number;
	totalRecognition: number;
	totalRecognitionScored: number;
};

const createQualityScoresFetcher = (params: { start_date: string; end_date: string }) => async () => {
	const http = await getHttpClient();
	return (await http.get<QualityScoresAPIData>(qualityScoresPath(), { headers, params })).data;
};

export const loadQualityScores = async (options: { startDate: Date; endDate: Date }) => {
	const queryClient = await getQueryClient();
	const currentMonthParams = datesToParams(options);
	const prevMonthParams = datesToParams({
		startDate: startOfMonth(subMonths(options.startDate, 1)),
		endDate: endOfMonth(subMonths(options.endDate, 1)),
	});

	// Fetch both current and previous month data
	return await Promise.all([
		queryClient.fetchQuery({
			queryKey: qualityScoresQueryKey(currentMonthParams),
			queryFn: createQualityScoresFetcher(currentMonthParams),
		}),
		queryClient.fetchQuery({
			queryKey: qualityScoresQueryKey(prevMonthParams),
			queryFn: createQualityScoresFetcher(prevMonthParams),
		}),
	]);
};

export const useQualityScores = (options: { startDate: Date; endDate: Date }) => {
	const params = datesToParams(options);

	return useQuery({
		queryKey: qualityScoresQueryKey(params),
		queryFn: createQualityScoresFetcher(params),
	});
};

{
	/* Top recognitions */
}
type TopRecognitionsAPIData = {
	id: string;
	attachments: Array<{
		gif: string;
		altText: string;
	}>;
	cachedFamilyAmount: number;
	cachedNumAddingOnUsersInFamily: number;
	commentCount: number;
	companyId: string;
	createdAt: string;
	customUserProperties: Array<{
		[property: string]: string[];
	}>;
	familyAmount: number;
	giver: UserAPIData;
	giverId: string;
	primaryHashtag: string;
	privacy: string;
	qualityScore: {
		dimensions: Array<{
			key: DimensionKey;
			matches: boolean;
			name: string;
			reason: string;
		}>;
		score: number;
	};
	reactionCount: number;
	reason: string;
	receiverIds: string[];
	receivers: UserAPIData[];
	updatedAt: string;
	via: string;
};

type UserAPIData = {
	id: string;
	displayName: string;
	username: string;
	allUsernames: string[];
	department: string;
	email: string;
	userMode: string;
	canReceive: boolean;
	profilePicUrl: string;
};

type TopRecognitionsAPIParams = {
	start_date: string;
	end_date: string;
	filter_direction?: string;
	filter_type?: string;
	filter_values?: string[];
};

const transformToScoredBonus = (data: TopRecognitionsAPIData): ScoredBonus => {
	const attachments = data.attachments.map((a) => ({
		gif: a.gif,
		videoThumbnailUrl: undefined,
		videoPosterUrl: undefined,
	}));

	const imageUrl = attachments[0]?.gif ?? '';

	return {
		id: data.id,
		reason: data.reason,
		qualityScore: data.qualityScore,
		attachments,
		createdAt: new Date(data.createdAt),
		familyAmount: data.familyAmount,
		giver: {
			id: data.giver.id,
			displayName: data.giver.displayName,
			profilePic: data.giver.profilePicUrl,
		},
		reactionCount: data.reactionCount,
		commentCount: data.commentCount,
		receivers: data.receivers.map((r) => ({
			id: r.id,
			displayName: r.displayName,
			profilePic: r.profilePicUrl,
		})),
		imageUrl,
		detailUrl: `/bonuses/${data.id}`,
	};
};

const createTopRecognitionsFetcher = (params: TopRecognitionsAPIParams) => async () => {
	const http = await getHttpClient();
	const data = (await http.get<TopRecognitionsAPIData[]>(topRecognitionsPath(), { headers, params })).data;

	return data.map(transformToScoredBonus);
};

type UseTopRecogntionsOptions = {
	startDate: Date;
	endDate: Date;
	filterDirection?: 'giver' | 'receiver';
	filterType?: string;
	filterValues?: string[];
};

const formatTopRecognitionParams = (options: UseTopRecogntionsOptions): TopRecognitionsAPIParams => {
	const formatted: TopRecognitionsAPIParams = {
		...datesToParams(options),
	};

	if (options.filterType && options.filterType !== 'hashtag' && options.filterValues?.length) {
		formatted.filter_direction = options.filterDirection ?? 'giver';
	}

	if (options.filterValues?.length && options.filterType) {
		if (options.filterType === 'hashtag' || options.filterType === 'team') {
			// With a filter type of hashtag or team, we just apply the filter values directly
			formatted.filter_type = options.filterType;
			formatted.filter_values = options.filterValues;
		} else {
			// But for other filter types, it's a custom property and we need to format the values with the property key
			formatted.filter_type = 'custom_property';
			formatted.filter_values = options.filterValues.map((value) => `${options.filterType}:${value}`);
		}
	}

	return formatted;
};

export const loadTopRecognitions = async (options: UseTopRecogntionsOptions) => {
	const queryClient = await getQueryClient();
	const params = formatTopRecognitionParams(options);

	return queryClient.fetchQuery({
		queryKey: topRecognitionsQueryKey(params),
		queryFn: createTopRecognitionsFetcher(params),
	});
};

export const useTopRecognitions = (options: UseTopRecogntionsOptions) => {
	const params = formatTopRecognitionParams(options);

	return useQuery({
		queryKey: topRecognitionsQueryKey(params),
		queryFn: createTopRecognitionsFetcher(params),
	});
};

type UseDepartmentAveragesOptions = {
	startDate: Date;
	endDate: Date;
};

type DepartmentAveragesAPIData = {
	department: string;
	dimensionAverages: Array<{
		averageMatchRate: number;
		averageMatchRatePercentage: number;
		key: DimensionKey;
		matches: 1;
		occurrences: number;
	}>;
	overallAveragePercentage: number;
	overallAverageScore: number;
	totalRecognitionScored: number;
};

export const useDepartmentAverages = (options: UseDepartmentAveragesOptions) => {
	const params = datesToParams(options);
	const http = useHttpClient();

	return useQuery({
		queryKey: departmentAveragesQueryKey(params),
		queryFn: async () => {
			return (await http.get<DepartmentAveragesAPIData[]>(departmentAveragesPath(), { params, headers })).data;
		},
	});
};

export const fetchGenerateDepartmentAveragesCsv = async (options: UseDepartmentAveragesOptions) => {
	const http = await getHttpClient();
	return http.get(departmentAveragesPath() + '.csv', { params: datesToParams(options) });
};
